import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { fetchTitilerPointValue } from '../utils/titilerPoint';

export type RiceGrowthStagePeriodId = '202609' | '202611';

export type RiceGrowthStageAreaRow = {
  stage: number;
  name: string;
  pixels: number;
  hectares: number;
  share: number;
};

export type RiceGrowthStagePeriod = {
  id: RiceGrowthStagePeriodId;
  label: string;
  cogUrl: string;
  bbox: [number, number, number, number];
  area: readonly RiceGrowthStageAreaRow[];
};

const STAGE_NAMES = [
  'No rice cycle detected',
  'Pre-planting / transplanting',
  'Vegetative',
  'Reproductive',
  'Ripening / maturity',
  'Harvested / post-harvest',
] as const;

/**
 * Nationwide wet-season rice growth stage.
 * Integer classes: 0 no rice | 1 pre-planting | 2 vegetative | 3 reproductive | 4 ripening | 5 harvested.
 */
export const RICE_GROWTH_STAGE_PERIODS: readonly RiceGrowthStagePeriod[] = [
  {
    id: '202611',
    label: 'Wet 2026 · Nov',
    cogUrl: 'https://digisaka.s3.amazonaws.com/wet2026_PHILIPPINES_202611.tiff',
    bbox: [114.278, 4.587, 126.605, 21.122],
    /** Nationwide class areas for Wet 2026 · Nov (`wet2026_PHILIPPINES_202611.tiff`). */
    area: [
      { stage: 0, name: STAGE_NAMES[0], pixels: 23363588, hectares: 2045017.1, share: 68.1 },
      { stage: 1, name: STAGE_NAMES[1], pixels: 0, hectares: 0, share: 0 },
      { stage: 2, name: STAGE_NAMES[2], pixels: 449235, hectares: 39148.6, share: 1.3 },
      { stage: 3, name: STAGE_NAMES[3], pixels: 2940483, hectares: 255393.9, share: 8.5 },
      { stage: 4, name: STAGE_NAMES[4], pixels: 2379634, hectares: 206992.9, share: 6.9 },
      { stage: 5, name: STAGE_NAMES[5], pixels: 5253330, hectares: 457981.7, share: 15.2 },
    ],
  },
  {
    id: '202609',
    label: 'Wet 2026 · Sep',
    cogUrl: 'https://digisaka.s3.amazonaws.com/wet2026_PHILIPPINES_202609.tiff',
    bbox: [116.928, 4.587, 126.604, 20.117],
    area: [
      { stage: 0, name: STAGE_NAMES[0], pixels: 25550165, hectares: 2235550.3, share: 74.5 },
      { stage: 1, name: STAGE_NAMES[1], pixels: 0, hectares: 0, share: 0 },
      { stage: 2, name: STAGE_NAMES[2], pixels: 656477, hectares: 57043.2, share: 1.9 },
      { stage: 3, name: STAGE_NAMES[3], pixels: 4567182, hectares: 396919.1, share: 13.2 },
      { stage: 4, name: STAGE_NAMES[4], pixels: 2807901, hectares: 244343.1, share: 8.1 },
      { stage: 5, name: STAGE_NAMES[5], pixels: 738813, hectares: 65117, share: 2.2 },
    ],
  },
];

export const DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID: RiceGrowthStagePeriodId = '202611';

export function getRiceGrowthStagePeriod(
  id: RiceGrowthStagePeriodId = DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID
): RiceGrowthStagePeriod {
  return RICE_GROWTH_STAGE_PERIODS.find((period) => period.id === id) ?? RICE_GROWTH_STAGE_PERIODS[0];
}

/** Default / newest month — kept so existing imports keep working. */
export const RICE_GROWTH_STAGE_COG_URL = getRiceGrowthStagePeriod().cogUrl;

/** Philippines extent (EPSG:4326) covering every published month. */
export const RICE_GROWTH_STAGE_BBOX: [number, number, number, number] = [114.278, 4.587, 126.605, 21.122];

export const RICE_GROWTH_STAGE_LABEL = getRiceGrowthStagePeriod().label;

/** Class 0 is "no rice". It is not painted so the basemap stays visible. */
export const RICE_GROWTH_STAGE_COLORS = [
  '#d9d9d9',
  '#4a90d9',
  '#3cb371',
  '#1f6f3a',
  '#e8a33a',
  '#8c6d62',
] as const;

/** Nationwide class areas for the default (newest) month. */
export const RICE_GROWTH_STAGE_AREA = getRiceGrowthStagePeriod().area;

export const RICE_GROWTH_STAGE_LEGEND = {
  colors: [...RICE_GROWTH_STAGE_COLORS],
  labels: [
    'No rice (not drawn)',
    'Pre-planting / transplanting',
    'Vegetative',
    'Reproductive',
    'Ripening / maturity',
    'Harvested',
  ],
  description:
    'Philippines rice crop growth stage for the 2026 wet season. Classes 1–5 are drawn; class 0 (no rice) is left transparent.',
};

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

/** Painted classes only. Class 0 is sent as nodata so it stays transparent. */
const RICE_GROWTH_STAGE_COLORMAP: Record<string, string> = {
  '1': RICE_GROWTH_STAGE_COLORS[1],
  '2': RICE_GROWTH_STAGE_COLORS[2],
  '3': RICE_GROWTH_STAGE_COLORS[3],
  '4': RICE_GROWTH_STAGE_COLORS[4],
  '5': RICE_GROWTH_STAGE_COLORS[5],
};

export async function fetchRiceGrowthStagePoint(
  lon: number,
  lat: number,
  signal?: AbortSignal,
  cogUrl: string = RICE_GROWTH_STAGE_COG_URL
): Promise<{ value: number | null; label: string; color: string }> {
  const raw = await fetchTitilerPointValue(cogUrl, lon, lat, { signal });
  const stage = raw == null ? null : Math.round(raw);
  if (stage == null || stage < 0 || stage > 5) {
    return { value: null, label: 'No data', color: '#9ca3af' };
  }
  return {
    value: stage,
    label: RICE_GROWTH_STAGE_LEGEND.labels[stage] ?? 'No data',
    color: RICE_GROWTH_STAGE_COLORS[stage] ?? '#9ca3af',
  };
}

export function buildRiceGrowthStageTileUrl(cogUrl: string = RICE_GROWTH_STAGE_COG_URL): string {
  const params = new URLSearchParams();
  params.set('url', cogUrl);
  params.set('bidx', '1');
  params.set('colormap', JSON.stringify(RICE_GROWTH_STAGE_COLORMAP));
  params.set('nodata', '0');
  params.set('resampling', 'nearest');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}
